import { Injectable } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { BehaviorSubject, Observable } from 'rxjs';

export interface JobCategory {
  id: string;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class JobCategoryService {
  private categoriesSubject = new BehaviorSubject<JobCategory[]>([]);
  private isLoading = false;
  private lastFetchTime = 0;
  private CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

  constructor(private supabaseService: SupabaseService) {}

  /**
   * Get job categories as an Observable
   */
  getCategories(): Observable<JobCategory[]> {
    // Check if we need to fetch fresh data
    const now = Date.now();
    const shouldFetch = 
      this.categoriesSubject.value.length === 0 || 
      (now - this.lastFetchTime) > this.CACHE_DURATION;

    if (shouldFetch && !this.isLoading) {
      this.loadCategories();
    }

    return this.categoriesSubject.asObservable();
  }

  /**
   * Get job categories as a Promise (for components that need immediate access)
   */
  async getCategoriesAsync(): Promise<JobCategory[]> {
    const now = Date.now();
    const shouldFetch = 
      this.categoriesSubject.value.length === 0 || 
      (now - this.lastFetchTime) > this.CACHE_DURATION;

    if (shouldFetch) {
      await this.loadCategories();
    }

    return this.categoriesSubject.value;
  }

  /**
   * Force refresh categories from database
   */
  async refreshCategories(): Promise<JobCategory[]> {
    await this.loadCategories();
    return this.categoriesSubject.value;
  }

  /**
   * Load categories from database
   */
  private async loadCategories(): Promise<void> {
    // Use predefined categories - no database queries needed!
    const predefinedCategories = [
      "Product Design",
      "UI/UX Design", 
      "UX Research",
      "Brand Design",
      "Design Engineering",
      "Design Leadership",
      "Graphic Design",
      "Interaction Design",
      "Motion Design",
      "Web Design"
    ];

    this.categoriesSubject.next(predefinedCategories.map(name => ({
      id: name.toLowerCase(),
      name: name
    })));
  }

  /**
   * Clear the cache (useful for testing or forced refresh)
   */
  clearCache(): void {
    this.categoriesSubject.next([]);
    this.lastFetchTime = 0;
  }
} 